import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  useCreateManufacturerDiscount,
  useDeleteManufacturerDiscount,
  useManufacturerDiscountCategories,
  useUpdateManufacturerDiscount,
} from "@/api/hooks";
import type { components } from "@/api/schema";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LoadingButton } from "@/components/loading-button";
import { ComposeInput } from "@/components/compose-field";
import {
  InlineFieldInput,
  InlineFieldTextarea,
} from "@/components/inline-field-group";
import { POInlineEditableField } from "@/components/po-inline-edit";
import { ActionPopover } from "@/components/action-popover";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SearchInput } from "@/components/search-input";

type Discount = components["schemas"]["ManufacturerDiscountRead"];

function errorMessage(error: unknown) {
  if (
    error &&
    typeof error === "object" &&
    "detail" in error &&
    typeof error.detail === "string"
  ) {
    return error.detail;
  }
  return "Could not save the discount. Try again.";
}

function parseModels(text: string) {
  return [
    ...new Set(
      text
        .split(/[\s,;]+/)
        .map((model) => model.trim())
        .filter(Boolean),
    ),
  ];
}

export function DiscountCategoryEditor({
  manuUuid,
  discount,
}: {
  manuUuid: string;
  discount?: Discount;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [chain, setChain] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  const [modelText, setModelText] = useState("");
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const canChooseCategories = !discount || discount.is_category_override;
  const categories = useManufacturerDiscountCategories(
    manuUuid,
    open && canChooseCategories,
  );
  const create = useCreateManufacturerDiscount(manuUuid);
  const update = useUpdateManufacturerDiscount(manuUuid);
  const pending = create.isPending || update.isPending;
  const prefix = discount ? `discount-${discount.id}` : "new-discount";
  const models = parseModels(modelText);
  const save = () => {
    const callbacks = {
      onSuccess: () => {
        setOpen(false);
        toast.success(
          discount ? "Discount updated" : "Discount category added",
        );
      },
      onError: (cause: unknown) => setError(errorMessage(cause)),
    };
    setError("");
    if (discount) {
      update.mutate(
        {
          discountId: discount.id,
          name: name.trim(),
          discount: chain.trim() || null,
          ...(canChooseCategories ? { aq_category_ids: selected, models } : {}),
          note: note.trim() || null,
        },
        callbacks,
      );
    } else {
      create.mutate(
        {
          name: name.trim(),
          discount: chain.trim(),
          aq_category_ids: selected,
          models,
          note: note.trim() || null,
        },
        callbacks,
      );
    }
  };
  const choices = [...(categories.data ?? [])];
  for (const category of discount?.aq_categories ?? []) {
    if (!choices.some((choice) => choice.id === category.id))
      choices.push(category);
  }
  const filtered = choices.filter((category) =>
    category.name.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (pending) return;
        setOpen(next);
        if (next) {
          setName(discount?.name ?? "");
          setChain(discount?.discount ?? "");
          setSelected(
            (discount?.aq_categories ?? []).map((category) => category.id),
          );
          setModelText(
            (discount?.member_products ?? [])
              .map((product) => product.model)
              .join("\n"),
          );
          setSearch("");
          setNote("");
          setError("");
        }
      }}
    >
      <DialogTrigger asChild>
        {discount ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Edit ${discount.name ?? "discount"}`}
          >
            <Pencil className="size-4" />
          </Button>
        ) : (
          <Button variant="outline">
            <Plus className="size-4" /> Add discount category
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {discount ? "Edit discount category" : "Add discount category"}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          {/* A saved category's values are click-to-edit; a new one's are boxes (USA-1580). Either
              way the dialog's Save sends them together. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`${prefix}-name`}>Name</Label>
              {discount ? (
                <POInlineEditableField
                  value={name}
                  placeholder="Not set"
                  ariaLabel="Name"
                  disabled={pending}
                  showEditIcon
                  buttonClassName="-mx-2"
                  onConfirm={setName}
                />
              ) : (
                <InlineFieldInput
                  id={`${prefix}-name`}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  disabled={pending}
                  maxLength={200}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${prefix}-chain`}>Discount</Label>
              {discount ? (
                <POInlineEditableField
                  value={chain}
                  placeholder="Not set"
                  ariaLabel="Discount"
                  disabled={pending}
                  showEditIcon
                  buttonClassName="-mx-2"
                  onConfirm={setChain}
                />
              ) : (
                <InlineFieldInput
                  id={`${prefix}-chain`}
                  value={chain}
                  onChange={(event) => setChain(event.target.value)}
                  placeholder="50 or 50/10/5"
                  required
                  disabled={pending}
                />
              )}
            </div>
          </div>
          {canChooseCategories && (
            <fieldset disabled={pending} className="space-y-2">
              <legend className="mb-2 text-sm font-medium">
                AQ categories ({selected.length})
              </legend>
              <SearchInput
                aria-label="Search AQ categories"
                placeholder="Search AQ categories"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <div className="max-h-60 overflow-y-auto rounded-md border p-1">
                {categories.isPending ? (
                  <p className="p-3 text-sm text-muted-foreground">
                    Loading categories…
                  </p>
                ) : categories.isError ? (
                  <div className="p-3 text-sm" role="alert">
                    Could not load AQ categories.{" "}
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => categories.refetch()}
                    >
                      Retry
                    </Button>
                  </div>
                ) : filtered.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">
                    No AQ categories found
                  </p>
                ) : (
                  filtered.map((category) => (
                    <label
                      key={category.id}
                      className="flex min-h-11 cursor-pointer items-center gap-3 rounded px-3 py-2 text-sm hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        className="size-4 shrink-0 accent-primary"
                        checked={selected.includes(category.id)}
                        onChange={(event) =>
                          setSelected((current) =>
                            event.target.checked
                              ? [...current, category.id]
                              : current.filter((id) => id !== category.id),
                          )
                        }
                      />
                      <span>{category.name}</span>
                    </label>
                  ))
                )}
              </div>
            </fieldset>
          )}
          {canChooseCategories && (
            <div className="space-y-2">
              <Label htmlFor={`${prefix}-models`}>
                Models ({models.length})
              </Label>
              {discount ? (
                <POInlineEditableField
                  value={modelText}
                  kind="textarea"
                  placeholder="No models"
                  ariaLabel={`Models (${models.length})`}
                  disabled={pending}
                  showEditIcon
                  allowWrap
                  buttonClassName="-mx-2 max-h-60 overflow-y-auto font-mono"
                  inputClassName="max-h-60 font-mono"
                  onConfirm={setModelText}
                />
              ) : (
                <InlineFieldTextarea
                  id={`${prefix}-models`}
                  value={modelText}
                  onChange={(event) => setModelText(event.target.value)}
                  placeholder="1364FRSF"
                  rows={4}
                  className="max-h-60 font-mono"
                  disabled={pending}
                />
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-note`}>Reason (optional)</Label>
            <ComposeInput
              id={`${prefix}-note`}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={pending}
              maxLength={1000}
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              loading={pending}
              loadingText="Saving…"
              disabled={
                !name.trim() ||
                (canChooseCategories &&
                  ((!selected.length && !models.length) ||
                    categories.isPending ||
                    categories.isError))
              }
            >
              Save
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RemoveDiscountCategory({
  manuUuid,
  discount,
}: {
  manuUuid: string;
  discount: Discount;
}) {
  const remove = useDeleteManufacturerDiscount(manuUuid);
  return (
    <ActionPopover
      align="end"
      trigger={
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Remove ${discount.name ?? "discount"}`}
        >
          <Trash2 className="size-4 text-muted-foreground" />
        </Button>
      }
      title={`Remove ${discount.name || "discount category"}?`}
      note={{
        label: "Reason (optional)",
        placeholder: "Reason (optional)",
        maxLength: 1000,
        singleLine: true,
      }}
      confirmLabel="Remove category"
      confirmVariant="destructive"
      pending={remove.isPending}
      onConfirm={(note, close) =>
        remove.mutate(
          { discountId: discount.id, note: note || undefined },
          { onSuccess: close },
        )
      }
    >
      <p className="text-xs text-muted-foreground">
        Its products will return to their AQ discount or the manufacturer’s
        default discount.
      </p>
    </ActionPopover>
  );
}
